import {
  memo,
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from "react";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  cn,
  formatPrice,
  getColorKrunshScore,
  getUserPosition,
  triggerEvent,
} from "@/lib/utils";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import type {
  LocationInput,
  MapPin,
  Restaurant,
  RestaurantDetails,
  RestaurantLeaderboardEntry,
} from "@/API";
import Text from "@/components/Text";
import Badge from "@/components/Badge";
import {
  BikeIcon,
  Heart,
  MoveDiagonalIcon,
  StarIcon,
  StoreIcon,
  X,
  XIcon,
} from "lucide-react";
import type { ViewMode } from "../..";
import { generatePath, useNavigate, useParams } from "react-router";
import useDebounce from "@/hooks/useDebounce";
import { updatePositionFilterExplorer } from "@/store/filterExplorerState";
import { getGooglePlaceState } from "@/store/getGooglePlaceState";
import { userRestaurantsState } from "@/store/userRestaurantsState";
import { restaurantDetailsState } from "@/store/restaurantDetailsState";
import KrunshScore from "@/components/KrunshScore";
import { Button } from "@/components/ui/button";
import AddRestaurantToFavorite from "@/components/dialogs/AddRestaurantToFavorite";
import DrawerAddRestaurantToFavorite from "@/components/drawers/DrawerAddRestaurantToFavorite";
import { Spinner } from "@/components/ui/spinner";
import type { DrawerState } from "../DrawerRestaurantsList";

type ClusterList = {
  position: { lat: number; lng: number };
  restaurants: MapPin[];
}[];

type GenerateCluster = {
  markerList: MapPin[];
  clusterList: ClusterList;
};

const getHeightMap = (drawerState: DrawerState) => {
  if (drawerState === "open") return 0;
  if (drawerState === "half-open") return "calc(100dvh / 2 - 140px)"; //return window.screen.height / 2 - 140;
  if (drawerState === "closed") return "calc(100dvh - 280px)";
};

const getPin = (
  restaurant: MapPin,
  userRestaurants: Restaurant[] | null,
  restaurantSelected: RestaurantDetails | null,
): string => {
  if (
    (restaurantSelected && restaurant.id === restaurantSelected.id) ||
    (userRestaurants && userRestaurants.find(({ id }) => id === restaurant.id))
  ) {
    return `/img/pins/store.svg`;
  }

  return `/img/pins/${restaurant.platform.toLowerCase()}.svg`;
};

const getStep = (zoom: number) => {
  if (zoom === 10) return 0.1;
  if (zoom === 11) return 0.05;
  if (zoom === 12) return 0.03;
  if (zoom === 13) return 0.01;
  if (zoom === 14) return 0.005;
  if (zoom === 15) return 0.001;
  if (zoom === 16) return 0.0005;
  if (zoom === 17) return 0.00001;

  if (zoom >= 18) return 0.000005;
  if (zoom < 3) return 8;
  if (zoom < 5) return 5;
  if (zoom < 6) return 2.5;
  if (zoom < 8) return 1.5;
  if (zoom < 10) return 0.5;
  return 0.1;
};

const filterRestaurantsCloseOfThisRestaurant = (
  restaurant: MapPin,
  restaurantTarget: MapPin,
  step: number,
): boolean => {
  const { latitude: latitudeTarget, longitude: longitudeTarget } =
    restaurantTarget.position;

  const minLat = latitudeTarget - step;
  const maxLat = latitudeTarget + step;
  const minLng = longitudeTarget - step;
  const maxLng = longitudeTarget + step;

  const { latitude, longitude } = restaurant.position;

  if (
    minLat < latitude &&
    latitude < maxLat &&
    minLng < longitude &&
    longitude < maxLng
  )
    return true;

  return false;
};

const generateCluster = (
  restaurants: MapPin[],
  step: number,
): GenerateCluster => {
  const markerList: MapPin[] = [];
  const clusterList: ClusterList = [];
  let restaurantsRest: MapPin[] = [...restaurants];

  while (restaurantsRest.length > 0) {
    const restaurantTarget = restaurantsRest.shift();

    if (restaurantTarget) {
      const restaurantsCloseOfThisRestaurant = restaurantsRest.filter(
        (restaurant) =>
          filterRestaurantsCloseOfThisRestaurant(
            restaurant,
            restaurantTarget,
            step,
          ),
      );

      if (restaurantsCloseOfThisRestaurant.length > 5) {
        const { latitude, longitude } = restaurantTarget.position;

        const minLat = latitude - step;
        const maxLat = latitude + step;
        const minLng = longitude - step;
        const maxLng = longitude + step;

        clusterList.push({
          position: {
            lat: (minLat + maxLat) / 2,
            lng: (minLng + maxLng) / 2,
          },
          restaurants: [...restaurantsCloseOfThisRestaurant, restaurantTarget],
        });

        restaurantsRest = restaurantsRest.filter(
          (restaurant) =>
            !filterRestaurantsCloseOfThisRestaurant(
              restaurant,
              restaurantTarget,
              step,
            ),
        );
      }

      if (restaurantsCloseOfThisRestaurant.length <= 5) {
        markerList.push(restaurantTarget);
      }
    }
  }

  return {
    markerList,
    clusterList,
  };
};

const center = getUserPosition();

type Props = {
  restaurants: MapPin[] | null;
  viewMode: ViewMode;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;
  pinsIsloading: boolean;
  drawerState?: DrawerState;
  setDrawerState?: Dispatch<SetStateAction<DrawerState>>;
};

const Map: FC<Props> = ({
  restaurants,
  viewMode,
  setViewMode,
  pinsIsloading,
  drawerState,
  setDrawerState,
}) => {
  const { sm } = useDetectSizeDevice();
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    id: "55f6b0cbd2ae313dcf7f2bce",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
  });
  const { restaurantId } = useParams<{ restaurantId?: string }>();

  const googlePlace = getGooglePlaceState();
  const { data: userRestaurants } = userRestaurantsState();
  const { data: RestaurantSelected } = restaurantDetailsState();

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [positionOverLay, setPositionOverlay] = useState(center);
  const [restaurantSelected, setRestaurantSelected] = useState<
    null | MapPin | RestaurantLeaderboardEntry
  >(null);
  const [markerList, setMarkerList] = useState<MapPin[]>([]);
  const [clusterList, setClusterList] = useState<ClusterList>([]);
  const [zoom, setZoom] = useState(10);
  const [position, setPosition] = useState<{
    topLeft: LocationInput;
    bottomRight: LocationInput;
  } | null>(null);
  const [addFavoriteThisReastaurantId, setAddFavoriteThisReastaurantId] =
    useState<string | null>(null);

  const debouncedPosition = useDebounce(position, 1000);

  const numberDeliverooRestaurant =
    restaurants?.filter((restaurant) => restaurant.platform === "DELIVEROO")
      ?.length ?? 0;
  const numberUbereatsRestaurant =
    restaurants?.filter((restaurant) => restaurant.platform === "UBEREATS")
      ?.length ?? 0;

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (RestaurantSelected && RestaurantSelected.position && map) {
      map.setCenter({
        lat: RestaurantSelected.position.latitude,
        lng: RestaurantSelected.position.longitude,
      });

      map.setZoom(17);
    }
  }, [RestaurantSelected, map]);

  useEffect(() => {
    if (restaurants) {
      let filteredRestaurants = restaurants;
      if (RestaurantSelected && RestaurantSelected.nearbyRestaurants) {
        const restaurantsNearbyId =
          RestaurantSelected.nearbyRestaurants.restaurants
            .filter(
              (nearbyRestaurant) =>
                nearbyRestaurant?.position &&
                nearbyRestaurant?.id !== RestaurantSelected.id,
            )
            .map((nearbyRestaurant) => nearbyRestaurant?.id);

        filteredRestaurants = restaurants.filter(
          (restaurant) => !restaurantsNearbyId.includes(restaurant.id),
        );
      }
      const clusterResult = generateCluster(
        filteredRestaurants.filter(
          ({ position }) => position?.latitude && position?.longitude,
        ),
        getStep(zoom),
      );

      if (clusterResult) {
        const { clusterList: CL, markerList: ML } = clusterResult;
        setClusterList([...CL]);
        setMarkerList([...ML]);
      }
    }
  }, [restaurants, zoom, RestaurantSelected]);

  useEffect(() => {
    if (googlePlace && map && googlePlace.location) {
      map.setCenter({
        lat: googlePlace.location.latitude,
        lng: googlePlace.location.longitude,
      });

      if (googlePlace.boundingBox && googlePlace.boundingBox) {
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            googlePlace.boundingBox.bottomRight.latitude,
            googlePlace.boundingBox.bottomRight.longitude,
          ),
          new google.maps.LatLng(
            googlePlace.boundingBox.topLeft.latitude,
            googlePlace.boundingBox.topLeft.longitude,
          ),
        );
        map.fitBounds(bounds);

        if (googlePlace.zoomUp) {
          map.setZoom(20);
        }
      }
    }
  }, [googlePlace, map]);

  useEffect(() => {
    if (debouncedPosition) {
      updatePositionFilterExplorer(debouncedPosition);
    }
  }, [debouncedPosition]);

  const updateZoom = useCallback(() => {
    if (map) {
      const newZoom = map.getZoom();

      if (newZoom) {
        setZoom(newZoom);
        triggerEvent("Map Moved", {
          zoom: map.getZoom(),
          move_type: "zoom",
        });
      }
    }
  }, [map]);

  const onClickCluster = useCallback(
    (position: { lat: number; lng: number }) => {
      if (map) {
        map.setCenter({
          lat: position.lat,
          lng: position.lng,
        });
        map.setZoom(zoom + 1);
      }
    },
    [map, zoom],
  );

  const updatePosition = useCallback(() => {
    if (map && drawerState !== "open") {
      const bounds = map.getBounds();

      if (bounds) {
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        if (northEast && southWest) {
          setPosition({
            topLeft: {
              latitude: northEast.lat(),
              longitude: northEast.lng(),
            },
            bottomRight: {
              latitude: southWest.lat(),
              longitude: southWest.lng(),
            },
          });
        }
      }

      if (map.getZoom()) {
        triggerEvent("Map Moved", {
          zoom: map.getZoom(),
          move_type: "drag",
        });
      }
    }
  }, [map, drawerState]);

  if (isLoaded)
    return (
      <>
        {sm ? (
          <AddRestaurantToFavorite
            open={!!addFavoriteThisReastaurantId}
            onClose={() => setAddFavoriteThisReastaurantId(null)}
            restaurantsId={
              addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
            }
          />
        ) : (
          <DrawerAddRestaurantToFavorite
            open={!!addFavoriteThisReastaurantId}
            onClose={() => setAddFavoriteThisReastaurantId(null)}
            restaurantsId={
              addFavoriteThisReastaurantId ? [addFavoriteThisReastaurantId] : []
            }
          />
        )}
        <div
          style={{
            position: "relative",
            width: sm ? (viewMode === "map" ? "100%" : "50%") : "100%",
            height: sm
              ? "calc(100vh - var(--height-header) - 68px)"
              : getHeightMap(drawerState ?? "half-open"),
            transition: "width 1s",
          }}
          className={cn(
            "bg-[#F7F8F9]",
            restaurantId ? "sm:p-[24px]" : "sm:px-[24px]",
          )}
        >
          <GoogleMap
            onClick={() => {
              setRestaurantSelected(null);
              setOpenOverlay(false);
            }}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              cameraControl: sm,
              streetViewControl: sm,
            }}
            clickableIcons={false}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onZoomChanged={() => updateZoom()}
            onBoundsChanged={() => {
              updatePosition();
            }}
            center={center}
            zoom={10}
            mapContainerStyle={{
              width: "100%",
              height: sm
                ? "calc(100vh - var(--height-header) - 68px)"
                : getHeightMap(drawerState ?? "half-open"),
              borderRadius: 16,
            }}
          >
            {markerList.map((restaurant) => (
              <Marker
                key={restaurant.id}
                icon={getPin(restaurant, userRestaurants, RestaurantSelected)}
                onClick={() => {
                  setRestaurantSelected(restaurant);
                  setPositionOverlay({
                    lat: restaurant.position.latitude,
                    lng: restaurant.position.longitude,
                  });
                  setOpenOverlay(true);
                  triggerEvent("Map Pin Clicked", {
                    store_id: restaurant.id,
                  });

                  if (!sm) {
                    setDrawerState?.("closed");
                  }
                  if (map) {
                    map.setCenter({
                      lat: restaurant.position.latitude,
                      lng: restaurant.position.longitude,
                    });
                  }
                }}
                clickable={true}
                position={{
                  lat: restaurant.position.latitude,
                  lng: restaurant.position.longitude,
                }}
              />
            ))}
            {clusterList.map(({ restaurants, position }) => (
              <Marker
                icon="/svg/cluster.svg"
                label={`${restaurants.length}`}
                onClick={() => {
                  onClickCluster(position);
                }}
                key={restaurants[0].id}
                position={{
                  lat: position.lat,
                  lng: position.lng,
                }}
              />
            ))}
            {RestaurantSelected?.nearbyRestaurants.restaurants.map(
              (nearbyRestaurant) => {
                if (!nearbyRestaurant) return null;

                return (
                  <Marker
                    key={nearbyRestaurant.id}
                    icon={
                      nearbyRestaurant.promotionCount > 0
                        ? "/img/pins/store-ranked-promo.svg"
                        : "/img/pins/store-ranked.svg"
                    }
                    onClick={() => {
                      setRestaurantSelected(nearbyRestaurant);
                      setPositionOverlay({
                        lat: nearbyRestaurant.position.latitude,
                        lng: nearbyRestaurant.position.longitude,
                      });
                      setOpenOverlay(true);
                      triggerEvent("Map Pin Clicked", {
                        store_id: nearbyRestaurant.id,
                      });
                      if (map) {
                        map.setCenter({
                          lat: nearbyRestaurant.position.latitude,
                          lng: nearbyRestaurant.position.longitude,
                        });
                      }
                    }}
                    clickable={true}
                    position={{
                      lat: nearbyRestaurant.position.latitude,
                      lng: nearbyRestaurant.position.longitude,
                    }}
                  />
                );
              },
            )}
            {openOverlay && sm && (
              <OverlayView position={positionOverLay} mapPaneName={"floatPane"}>
                <div
                  className="bg-[#FFFFFF] rounded-[12px] py-[16px] px-[24px] flex flex-col w-[300px] cursor:pointer"
                  onClick={() => {
                    if (restaurantSelected) {
                      setViewMode("list");
                      navigate(
                        generatePath("/explorer/restaurant/:restaurantId", {
                          restaurantId: restaurantSelected.id,
                        }),
                      );
                    }
                  }}
                >
                  {restaurantSelected && (
                    <>
                      <div className="flex gap-[12px] items-center">
                        <img
                          className="h-[40px] w-[40px] rounded-[3px]"
                          src={restaurantSelected?.imageUrl ?? undefined}
                          alt={`image of ${restaurantSelected.name}`}
                        />
                        <div className="flex flex-col gap-[4px]">
                          <Text fontWeight={600} fontSize={14}>
                            {restaurantSelected.name}
                          </Text>
                          <Text fontWeight={400} fontSize={12}>
                            {restaurantSelected.address}
                          </Text>
                        </div>
                      </div>
                      <div className="flex items-center gap-[8px] mt-[12px] relative">
                        <img
                          alt={`icon of ${restaurantSelected.platform}`}
                          src={`/partner/${restaurantSelected.platform.toLowerCase()}.png`}
                          className="w-[24px] h-[24px] rounded-[54px]"
                        />
                        <div
                          className="bg-[white] ml-[-20px] h-[24px] w-[24px] rounded-[50%] flex items-center justify-center"
                          style={{
                            border: `2px solid ${getColorKrunshScore(restaurantSelected.krunshScore)}`,
                          }}
                        >
                          <Text
                            fontSize={12}
                            fontWeight={600}
                            color={getColorKrunshScore(
                              restaurantSelected.krunshScore,
                            )}
                          >
                            {restaurantSelected.krunshScore}
                          </Text>
                        </div>
                        <Badge>
                          <StarIcon height={16} width={16} color="#758195" />
                          <Text fontWeight={600} fontSize={14}>
                            {restaurantSelected.rating}
                          </Text>
                        </Badge>
                        <Badge>
                          <BikeIcon height={16} width={16} color="#758195" />
                          <Text
                            fontWeight={600}
                            fontSize={14}
                          >{`${restaurantSelected.deliveryTime} min`}</Text>
                        </Badge>
                      </div>
                    </>
                  )}
                </div>
              </OverlayView>
            )}

            {!sm && (
              <div
                className="absolute p-[16px] z-[3]"
                style={{
                  left: 0,
                  right: 0,
                  bottom: openOverlay ? 0 : -400,
                  transition: "bottom 0.3s",
                }}
              >
                {openOverlay && restaurantSelected && (
                  <div className="bg-[white] rounded-[16px]">
                    <div
                      className="p-[6px] relative"
                      onClick={() => {
                        navigate(
                          generatePath("/explorer/restaurant/:restaurantId", {
                            restaurantId: restaurantSelected.id,
                          }),
                        );
                      }}
                    >
                      <img
                        src={restaurantSelected?.imageUrl ?? undefined}
                        alt={`image of ${restaurantSelected.name}`}
                        className="aspect-3/2 object-cover rounded-[16px]"
                      />

                      <img
                        className="absolute h-[32px] w-[32px] top-[16px] left-[16px] rounded-[50%]"
                        src={`partner/${restaurantSelected?.platform?.toLowerCase()}.png`}
                        alt={restaurantSelected?.platform}
                      />

                      <div className="absolute right-[16px] top-[16px] flex gap-[12px]">
                        <Button
                          variant="secondaryFilled"
                          size="iconRounded"
                          onClick={(e) => {
                            setAddFavoriteThisReastaurantId(
                              restaurantSelected.id,
                            );
                            e.stopPropagation();
                          }}
                        >
                          <Heart color="#44546F" height={12} width={12} />
                        </Button>
                        <Button
                          variant="secondaryFilled"
                          size="iconRounded"
                          onClick={(e) => {
                            setOpenOverlay(false);
                            setRestaurantSelected(null);
                            e.stopPropagation();
                          }}
                        >
                          <XIcon color="#44546F" height={12} width={12} />
                        </Button>
                      </div>

                      <div className="absolute left-[16px] bottom-[16px] flex items-center gap-[4px]">
                        <Badge>
                          <StarIcon height={16} width={16} color="#758195" />
                          <Text fontWeight={600} fontSize={14}>
                            {restaurantSelected.rating}
                          </Text>
                        </Badge>
                        <Badge>
                          <BikeIcon height={16} width={16} color="#758195" />
                          <Text
                            fontWeight={600}
                            fontSize={14}
                          >{`${restaurantSelected.deliveryTime} min`}</Text>
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center p-[16px] gap-[12px]">
                      <KrunshScore value={80} />
                      <div className="flex flex-col gap-[4px]">
                        <Text fontSize={14} fontWeight={600}>
                          {restaurantSelected.name}
                        </Text>
                        <Text
                          fontSize={14}
                          fontWeight={400}
                          className="truncate"
                        >
                          {restaurantSelected.address}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </GoogleMap>

          <div
            onClick={() => {
              setViewMode((prevState) =>
                prevState === "map" ? "list" : "map",
              );
            }}
            className="cursor-pointer hidden sm:flex absolute justify-center items-center h-[40px] w-[40px] right-[34px] bg-[#F1F2F4] rounded-[50%] shadow-md z-[10]"
            style={{
              top: restaurantId ? 32 : 10,
            }}
          >
            {viewMode === "map" ? (
              <X color="#44546F" />
            ) : (
              <MoveDiagonalIcon color="#44546F" />
            )}
          </div>

          {sm && (
            <div
              className="absolute left-[38px] flex gap-[8px]"
              style={{
                top: restaurantId ? 38 : 14,
              }}
            >
              {pinsIsloading ? (
                <Spinner />
              ) : (
                <>
                  <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
                    <StoreIcon height={16} width={16} color="#758195" />
                    <Text fontWeight={600} fontSize={14}>
                      {Array.isArray(restaurants)
                        ? formatPrice(restaurants.length.toString())
                        : 0}
                    </Text>
                  </div>
                  <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
                    <img
                      height={16}
                      width={16}
                      className="rounded-[4px]"
                      src="/partner/ubereats.png"
                      alt="ubereats"
                    />

                    <Text fontSize={14} fontWeight={600}>
                      {`${
                        numberUbereatsRestaurant === 0
                          ? 0
                          : (
                              (numberUbereatsRestaurant /
                                (numberUbereatsRestaurant +
                                  numberDeliverooRestaurant)) *
                              100
                            ).toFixed(0)
                      } %`}
                    </Text>
                  </div>
                  <div className="flex bg-[#FCFCFD] p-[8px] rounded-[8px] items-center gap-[4px]">
                    <img
                      height={16}
                      width={16}
                      className="rounded-[4px]"
                      src="/partner/deliveroo.png"
                      alt="deliveroo"
                    />

                    <Text fontSize={14} fontWeight={600}>
                      {`${
                        numberDeliverooRestaurant === 0
                          ? 0
                          : (
                              (numberDeliverooRestaurant /
                                (numberUbereatsRestaurant +
                                  numberDeliverooRestaurant)) *
                              100
                            ).toFixed(0)
                      } %`}
                    </Text>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </>
    );
};

export default memo(Map);
